<template>
  <div class="w-full flex flex-col scrollbar">
    <!-- Fixed section: Search and Table Header -->
    <div class="fixed-section sticky top-[61px] w-full bg-bg-base z-20 pt-4">
      <!-- Search input -->
      <div class="flex flex-col gap-4 lg:flex-row lg:items-center mb-4">
        <div class="flex flex-1">
          <search-input
            v-model="state.currentSearch"
            :placeholder="$t('profiles.backed_campaigns.search_placeholder')"
            @search="handleSearch"
          />
        </div>
      </div>

      <!-- Table header: 只在 XL+ 顯示 -->
      <div class="hidden xl:grid xl:grid-cols-[18fr_8fr_25fr_25fr_20fr] gap-x-4 mb-2 h-5 text-12px font-bold text-gray-160">
        <div>{{ $t('profiles.backed_campaigns.campaign_title') }}</div>
        <div>{{ $t('profiles.backed_campaigns.pledge_price') }}</div>
        <div>{{ $t('profiles.backed_campaigns.rewards_title') }}</div>
        <div>{{ $t('profiles.backed_campaigns.shipping_info') }}</div>
        <div>{{ $t('profiles.backed_campaigns.status') }}</div>
      </div>
      <hr class='hidden xl:block border-gray-400 my-2'>
    </div>

    <!-- 內容區域 -->
    <div class="content-section flex-1 flex flex-col">
      <!-- 載入狀態 -->
      <div v-if="state.initialLoading" class="flex justify-center items-center min-h-[400px]">
        <img src="../../images/icons/loading.png" class="w-6 h-6 icon loading-icon"/>
      </div>

      <!-- 無資料狀態 -->
      <div v-else-if="filteredOrders.length === 0 && !state.currentSearch" class="flex flex-col items-center justify-center min-h-[400px] text-center">
        <img src="../../images/file.svg" class="block w-12 h-12 mb-4" />
        <p class="font-medium text-gray-100">
          {{ $t('profiles.backed_campaigns.no_record') }}
        </p>
      </div>

      <!-- 無搜尋結果狀態 -->
      <div v-else-if="filteredOrders.length === 0 && state.currentSearch" class="flex flex-col items-center justify-center min-h-[400px] text-center">
        <img src="../../images/file.svg" class="block w-12 h-12 mb-4" />
        <h3 class="text-16px font-medium mb-2 text-gray-100">
          {{ $t('profiles.backed_campaigns.no_search_results') }}
        </h3>
        <button @click="clearSearch" class="btn btn-primary">
          {{ $t('profiles.backed_campaigns.clear_search') }}
        </button>
      </div>

      <!-- 資料列表 -->
      <div v-else class="model-container flex-1 overflow-auto scrollbar -mr-2 backed-campaigns-list">
        <div v-for="data in filteredOrders" :key="data.order.id" class="campaign-row">
          <div class="campaign-grid">
            <!-- Campaign 標題 -->
            <div class="campaign-title">
              <div v-if="data.campaign_data.campaign" class="font-medium hover:underline">
                <a :href="`/campaigns/${data.campaign_data.campaign.id}`" target="_blank">
                  {{ data.campaign_data.campaign.title }}
                </a>
              </div>
              <div v-else class="text-gray-300">
                {{ $t('profiles.backed_campaigns.campaign_no_longer_available') }}
              </div>
            </div>

            <!-- Pledge Total -->
            <div class="pledge-total">
              {{ formatCurrency(data.order.total_price) }}
            </div>

            <!-- Rewards -->
            <div class="rewards-col">
              <div
                v-for="rewardData in data.campaign_data.grouped_rewards"
                :key="rewardData.reward.id"
                class="reward-row"
              >
                <span>{{ rewardData.reward.title }} ×{{ rewardData.count }}</span>
                <span class="ml-4 flex-shrink-0">{{ formatCurrency(rewardData.subtotal) }}</span>
              </div>
            </div>

            <!-- Shipping Info -->
            <div class="shipping-col">
              <span class="xl:hidden text-12px text-gray-160 font-medium mb-1">{{ $t('profiles.backed_campaigns.shipping_info') }}</span>
              <template v-if="hasShippingInfo(data.shipping_info)">
                <div v-if="data.shipping_info.name" class="text-14px">{{ data.shipping_info.name }}</div>
                <div v-if="data.shipping_info.phone" class="text-14px">{{ data.shipping_info.phone }}</div>
                <div v-if="formatFullAddress(data.shipping_info)" class="text-14px">
                  {{ formatFullAddress(data.shipping_info) }}
                </div>
              </template>
              <span v-else class="text-gray-400 text-14px italic">
                {{ $t('profiles.backed_campaigns.no_shipping_info') }}
              </span>
              <button
                @click="openShippingModal(data.order.id, data.shipping_info)"
                class="btn btn-primary mt-2 text-sm px-3 py-1 w-fit"
                :disabled="!isShippingEditable(data.campaign_data.campaign)"
                :class="{ 'opacity-50 cursor-not-allowed': !isShippingEditable(data.campaign_data.campaign) }"
              >
                {{ $t('profiles.backed_campaigns.update') }}
              </button>
            </div>

            <!-- Status -->
            <div class="status">
              <div class="flex flex-col items-start gap-2">
                <span :class="getStatusStyle(data).class" :style="getStatusStyle(data).style">
                  {{ getOrderStatus(data) }}
                </span>
                <a
                  :href="`/profile/backed_campaigns/${data.order.id}`"
                  class="text-12px text-gray-100 hover:text-white underline"
                >
                  {{ $t('profiles.backed_campaigns.view_details') }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Shipping Modal -->
    <shipping-modal
      :isOpen="shippingModal.isOpen"
      :initialData="shippingModal.initialData"
      :loading="shippingModal.loading"
      :error="shippingModal.error"
      @close="closeShippingModal"
      @submit="saveShippingInfo"
    />
  </div>
</template>

<script>
import { defineComponent, reactive, computed, onMounted, getCurrentInstance } from "vue"
import debounce from 'lodash/debounce'

export default defineComponent({
  setup() {
    const instance = getCurrentInstance()
    const $t = instance?.proxy?.$t || (() => '')
    const t = (key, options = {}) => {
      if (window.i18nextGlobal) {
        return window.i18nextGlobal(key, options)
      }
      return $t(key, options) || key
    }

    const state = reactive({
      loading: false,
      initialLoading: true,
      currentSearch: '',
      orders: []
    })

    const shippingModal = reactive({
      isOpen: false,
      loading: false,
      error: null,
      orderId: null,
      initialData: {}
    })

    const filteredOrders = computed(() => {
      if (!state.currentSearch) {
        return state.orders
      }

      const searchTerm = state.currentSearch.toLowerCase().trim()
      return state.orders.filter(data => {
        const campaignTitle = data.campaign_data?.campaign?.title?.toLowerCase() || ''
        return campaignTitle.includes(searchTerm)
      })
    })

    const fetchInitialData = async () => {
      try {
        const response = await fetch('/api/v1/profile/backed_campaigns')
        if (response.ok) {
          const data = await response.json()
          state.orders = data.data || []
        }
      } catch (error) {
        console.error('Error fetching backed campaigns:', error)
      } finally {
        state.initialLoading = false
      }
    }

    const handleSearch = debounce((keyword) => {
      state.currentSearch = keyword
    }, 300)

    const clearSearch = () => {
      state.currentSearch = ''
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount)
    }

    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      })
    }

    // Shipping helpers
    const hasShippingInfo = (shippingInfo) => {
      return shippingInfo && Object.keys(shippingInfo).length > 0
    }

    const formatFullAddress = (shippingInfo) => {
      if (!shippingInfo) return ''
      return [
        shippingInfo.address1,
        shippingInfo.address2,
        shippingInfo.city,
        shippingInfo.state,
        shippingInfo.zip,
        shippingInfo.country
      ].filter(Boolean).join(', ')
    }

    const isShippingEditable = (campaign) => {
      if (!campaign?.end_at) return false
      const deadlineDate = new Date(new Date(campaign.end_at).getTime() + 7 * 24 * 60 * 60 * 1000)
      return new Date() <= deadlineDate
    }

    // Shipping modal methods
    const openShippingModal = (orderId, shippingInfo) => {
      shippingModal.orderId = orderId
      shippingModal.initialData = shippingInfo || {}
      shippingModal.error = null
      shippingModal.isOpen = true
    }

    const closeShippingModal = () => {
      shippingModal.isOpen = false
      shippingModal.error = null
    }

    const saveShippingInfo = async (formData) => {
      try {
        shippingModal.loading = true
        shippingModal.error = null

        const response = await fetch(`/api/v1/profile/backed_campaigns/${shippingModal.orderId}/shipping_info`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          body: JSON.stringify({ shipping_info: formData })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to update shipping info')
        }

        await fetchInitialData()
        closeShippingModal()
      } catch (error) {
        console.error('Error saving shipping info:', error)
        shippingModal.error = error.message
      } finally {
        shippingModal.loading = false
      }
    }

    // 狀態映射常量
    const ORDER_STATUS = {
      REFUND: {
        pending_refund: 'profiles.backed_campaigns.order_status.refund_pending',
        refunded: 'profiles.backed_campaigns.order_status.refunded',
      },
      ORDER: {
        created: 'profiles.backed_campaigns.order_status.pending_payment',
        pending_payment: 'profiles.backed_campaigns.order_status.pending_payment',
        cancelled: 'profiles.backed_campaigns.order_status.cancelled',
        interrupted: 'profiles.backed_campaigns.order_status.interrupted',
      },
      CAMPAIGN: {
        active: 'profiles.backed_campaigns.order_status.successfully_backed',
        draft: 'profiles.backed_campaigns.order_status.crowdfunding_success',
        prototype: 'profiles.backed_campaigns.order_status.in_production',
        production: 'profiles.backed_campaigns.order_status.manufacturing',
        shipping: 'profiles.backed_campaigns.order_status.shipped',
      }
    }

    const STATUS_COLORS = {
      SUCCESS: '#29cb00',
      PROGRESS: '#ffb72f',
      SHIPPING: '#4f84eb',
      REFUND: '#ff7474',
      CANCELLED: '#99a0a8',
      DEFAULT: '#99a0a8'
    }

    const isCampaignEnded = (campaign) => {
      if (!campaign?.end_at) return true
      return new Date() > new Date(campaign.end_at)
    }

    const getProductionStatusKey = (productionState) => {
      return ORDER_STATUS.CAMPAIGN[productionState] || ORDER_STATUS.CAMPAIGN.draft
    }

    const getPaidOrderStatusKey = (campaign) => {
      if (campaign?.visible) return ORDER_STATUS.CAMPAIGN.active
      if (isCampaignEnded(campaign)) return getProductionStatusKey(campaign?.production_state)
      return ORDER_STATUS.CAMPAIGN.draft
    }

    const getOrderStatusKey = (data) => {
      const order = data.order
      const campaign = data.campaign_data.campaign
      if (ORDER_STATUS.REFUND[order.refund_status]) return ORDER_STATUS.REFUND[order.refund_status]
      if (ORDER_STATUS.ORDER[order.state]) return ORDER_STATUS.ORDER[order.state]
      if (order.state === 'paid') return getPaidOrderStatusKey(campaign)
      return null
    }

    const getOrderStatus = (data) => {
      const statusKey = getOrderStatusKey(data)
      return statusKey ? t(statusKey) : data.order.state
    }

    const getStatusColorByKey = (statusKey) => {
      const colorMapping = {
        [ORDER_STATUS.CAMPAIGN.active]: STATUS_COLORS.SUCCESS,
        [ORDER_STATUS.CAMPAIGN.draft]: STATUS_COLORS.SUCCESS,
        [ORDER_STATUS.CAMPAIGN.prototype]: STATUS_COLORS.PROGRESS,
        [ORDER_STATUS.CAMPAIGN.production]: STATUS_COLORS.PROGRESS,
        [ORDER_STATUS.CAMPAIGN.shipping]: STATUS_COLORS.SHIPPING,
        [ORDER_STATUS.ORDER.pending_payment]: STATUS_COLORS.PROGRESS,
        [ORDER_STATUS.REFUND.pending_refund]: STATUS_COLORS.REFUND,
        [ORDER_STATUS.REFUND.refunded]: STATUS_COLORS.REFUND,
        [ORDER_STATUS.ORDER.cancelled]: STATUS_COLORS.CANCELLED,
        [ORDER_STATUS.ORDER.interrupted]: STATUS_COLORS.CANCELLED
      }
      return colorMapping[statusKey] || STATUS_COLORS.DEFAULT
    }

    const getStatusStyle = (data) => {
      const statusKey = getOrderStatusKey(data)
      const color = getStatusColorByKey(statusKey)
      const baseClass = "font-medium text-center inline-block py-1 px-2 rounded-full border text-14px"
      return {
        class: baseClass,
        style: {
          color: color,
          backgroundColor: `color-mix(in srgb, ${color}, transparent 80%)`,
          borderColor: color
        }
      }
    }

    onMounted(() => {
      fetchInitialData()
    })

    return {
      state,
      shippingModal,
      filteredOrders,
      handleSearch,
      clearSearch,
      formatCurrency,
      formatDate,
      hasShippingInfo,
      formatFullAddress,
      isShippingEditable,
      openShippingModal,
      closeShippingModal,
      saveShippingInfo,
      getOrderStatus,
      getStatusStyle,
      t
    }
  }
})
</script>

